/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import KpiCarouselApi2TimeSeries from '@/components/api/KpiCarouselApi2TimeSeries';
import KpiValueApi2TimeSeries from '@/components/api/KpiValueApi2TimeSeries';
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';

/* Import configuration */
import {
    KPI_CAROUSEL_API2_TIME_SERIES_EXAMPLES,
    KPI_VALUE_API2_TIME_SERIES_EXAMPLES,
} from '@/components/tabs/layouts/layoutExamples';

interface KpiApi2TimeSeriesLayoutsTabProps {
    theme: 'light' | 'dark';
}

function KpiApi2TimeSeriesLayoutsTab({ theme }: KpiApi2TimeSeriesLayoutsTabProps) {
    const [selectedKpiValueExampleIndex, setSelectedKpiValueExampleIndex] = useState(0);
    const [selectedKpiCarouselExampleIndex, setSelectedKpiCarouselExampleIndex] = useState(0);
    const selectedKpiValueExample = KPI_VALUE_API2_TIME_SERIES_EXAMPLES[selectedKpiValueExampleIndex];
    const selectedKpiCarouselExample = KPI_CAROUSEL_API2_TIME_SERIES_EXAMPLES[selectedKpiCarouselExampleIndex];

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="kpi-api2-time-series" />

            <section className="layouts-section">
                <h3>KpiValueApi2TimeSeries</h3>
                <p>Loads time series data from API2, aggregates it and renders a shared KPI card.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-value-api2-time-series-example">
                        API value example
                    </label>
                    <select
                        id="kpi-value-api2-time-series-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiValueExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiValueExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_VALUE_API2_TIME_SERIES_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift"><button type="button" className="tab tab-active">JSX</button></div>
                    <CodeBlock className="layouts-code--tabbed" roundedTop={false} code={selectedKpiValueExample.code} theme={theme} />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiValueApi2TimeSeries
                        aggregation={selectedKpiValueExample.props.aggregation}
                        calc={selectedKpiValueExample.props.calc}
                        calcCode={selectedKpiValueExample.props.calcCode}
                        conversion={selectedKpiValueExample.props.conversion}
                        fractionDigits={selectedKpiValueExample.props.fractionDigits}
                        from={selectedKpiValueExample.props.from}
                        info={selectedKpiValueExample.props.info}
                        series={selectedKpiValueExample.props.series}
                        showDate={selectedKpiValueExample.props.showDate}
                        showLegend={selectedKpiValueExample.props.showLegend}
                        title={selectedKpiValueExample.props.title}
                        to={selectedKpiValueExample.props.to}
                        trafficLight={selectedKpiValueExample.props.trafficLight}
                        unit={selectedKpiValueExample.props.unit}
                        urn={selectedKpiValueExample.props.urn}
                        valueLabels={selectedKpiValueExample.props.valueLabels}
                    />
                </section>
            </section>

            <section className="layouts-section">
                <h3>KpiCarouselApi2TimeSeries</h3>
                <p>Loads multiple API2-based KPI values and presents them in a shared carousel.</p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="kpi-carousel-api2-time-series-example">
                        API carousel example
                    </label>
                    <select
                        id="kpi-carousel-api2-time-series-example"
                        className="select select-bordered w-auto"
                        value={selectedKpiCarouselExampleIndex}
                        onChange={(event) => {
                            setSelectedKpiCarouselExampleIndex(Number(event.target.value));
                        }}
                    >
                        {KPI_CAROUSEL_API2_TIME_SERIES_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>{example.label}</option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift"><button type="button" className="tab tab-active">JSX</button></div>
                    <CodeBlock className="layouts-code--tabbed" roundedTop={false} code={selectedKpiCarouselExample.code} theme={theme} />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel layouts-demo-panel--value">
                    <KpiCarouselApi2TimeSeries
                        autoAdvanceMs={selectedKpiCarouselExample.props.autoAdvanceMs}
                        from={selectedKpiCarouselExample.props.from}
                        info={selectedKpiCarouselExample.props.info}
                        items={selectedKpiCarouselExample.props.items}
                        showDate={selectedKpiCarouselExample.props.showDate}
                        showLegend={selectedKpiCarouselExample.props.showLegend}
                        to={selectedKpiCarouselExample.props.to}
                    />
                </section>
            </section>
        </div>
    );
}

export default KpiApi2TimeSeriesLayoutsTab;
